import { Location } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginRedirectService } from '@core/services/login-redirect.service';
import { MetadataService } from '@core/services/metadata.service';
import { LoginForm } from '@shared/auth-forms/login-form/login-form.component';
import { CtaButtonComponent } from '@shared/cta-button/cta-button.component';
import { CustomInputComponent } from '@shared/custom-input/custom-input.component';
import { LinkComponent } from '@shared/link/link.component';
import { SaveButtonComponent } from '@shared/save-button/save-button.component';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UserService } from '../../services/user.service';

@Component({
    selector: 'app-user-login',
    templateUrl: './user-login.component.html',
    imports: [
        ReactiveFormsModule,
        CustomInputComponent,
        SaveButtonComponent,
        LinkComponent,
        CtaButtonComponent,
    ],
})
export class UserLoginComponent implements OnInit {
    private metadataService = inject(MetadataService);
    private location = inject(Location);
    private userService = inject(UserService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private loginRedirectService = inject(LoginRedirectService);
    private formBuilder = inject(FormBuilder);

    loginError = signal<string | null>(null);
    isSubmittingForm = signal(false);

    loginForm: FormGroup<LoginForm> = this.formBuilder.group({
        email: new FormControl('', [Validators.required, Validators.email]),
        password: new FormControl('', [
            Validators.required,
            Validators.minLength(6),
        ]),
    });

    ngOnInit(): void {
        this.metadataService.setTitle('Sign in');
        this.metadataService.setColor('#424242');
    }

    goBack() {
        this.location.back();
    }

    onSubmit() {
        this.loginForm.markAllAsTouched();
        this.isSubmittingForm.set(true);
        this.loginError.set(null);
        const values = this.loginForm.value;
        if (!this.loginForm.invalid) {
            this.userService
                .login(values.email, values.password)
                .pipe(
                    map(() =>
                        this.loginRedirectService.retrieve(this.route.snapshot)
                    ),
                    switchMap(url => this.router.navigateByUrl(url)),
                    catchError(error => {
                        this.handleError(error);
                        return of(null);
                    })
                )
                .subscribe();
        } else {
            this.isSubmittingForm.set(false);
            this.loginError.set(
                'Please fill in all required fields correctly.'
            );
        }
    }

    handleError(error) {
        this.isSubmittingForm.set(false);
        if (error.status == 400) {
            this.loginError.set(
                'Seems like your email and/or password is wrong. Please try a different combination.'
            );
        }
    }
}
